import React, { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Users, MessageSquare, BarChart3,
  Settings, LogOut, Sun, Moon, Bell, Building2, Plus,
  ClipboardList, FolderOpen, Briefcase, Wallet, Receipt,
  UserCog, ChevronDown, ChevronRight, Ticket, Activity, FileText, Users2,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import api from "@/lib/api";
import { useLogo } from "@/lib/useLogo";

const SERVICE_EMOJI = { "ITR": "📊", "GST": "🧾", "MSME Loan": "🏢", "Business Loan": "💼", "Personal Loan": "💰", "Insurance": "🛡️", "Other": "📁" };

function NavItem({ to, icon: Icon, label, testId, emoji }) {
  return (
    <NavLink to={to} end data-testid={testId} className={({ isActive }) => `sidebar-item ${isActive ? "active" : ""}`}>
      {emoji ? <span className="w-[18px] text-center">{emoji}</span> : <Icon size={18} />}
      <span>{label}</span>
    </NavLink>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();
  const logo = useLogo();
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifs, setNotifs] = useState({ items: [], unread: 0 });
  const [servicesOpen, setServicesOpen] = useState(true);
  const [enabledServices, setEnabledServices] = useState([]);

  useEffect(() => {
    api.get("/meta").then((r) => setEnabledServices(r.data.service_types)).catch(() => {});
  }, []);

  const fetchNotifs = async () => {
    try {
      const r = await api.get("/notifications");
      setNotifs(r.data);
    } catch (e) {
      console.warn("Failed to fetch notifications", e);
    }
  };

  useEffect(() => {
    fetchNotifs();
    const t = setInterval(fetchNotifs, 15000);
    return () => clearInterval(t);
  }, []);

  const markRead = async () => {
    await api.post("/notifications/mark-read");
    fetchNotifs();
  };

  const isAdmin = user?.role === "admin";
  const isEmployee = user?.role === "employee";
  const isConnector = user?.role === "connector";
  const perms = user?.permissions || [];
  const empServices = user?.services || [];
  const visibleServices = isEmployee && empServices.length ? empServices : enabledServices;

  return (
    <div className="min-h-screen flex bg-background text-foreground">
      <aside className="w-64 shrink-0 border-r border-border bg-card flex flex-col" data-testid="sidebar">
        <div className="h-16 flex items-center gap-3 px-5 border-b border-border">
          <img src={logo} alt="Nayan Services" className="w-9 h-9 rounded-lg object-cover shadow" />
          <div>
            <div className="font-heading font-bold text-base leading-tight">NAYAN SERVICES</div>
            <div className="text-[11px] text-muted-foreground">CRM · Financial Services</div>
          </div>
        </div>

        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground px-3 pt-2 pb-1">Main</div>
          <NavItem to="/" icon={LayoutDashboard} label="Dashboard" testId="nav-dashboard" />

          {(isAdmin || (isEmployee && perms.includes("view_leads"))) && (
            <NavItem to="/leads" icon={ClipboardList} label="All Leads" testId="nav-leads" />
          )}
          {isConnector && (
            <>
              <NavItem to="/new-order" icon={Plus} label="New Order" testId="nav-new-order" />
              <NavItem to="/leads" icon={FolderOpen} label="My Orders" testId="nav-my-orders" />
            </>
          )}

          {/* Services submenu */}
          {(isAdmin || (isEmployee && perms.includes("view_leads"))) && (
            <>
              <button
                type="button"
                onClick={() => setServicesOpen((v) => !v)}
                className="sidebar-item w-full justify-between"
                data-testid="nav-services-toggle"
              >
                <span className="flex items-center gap-3"><Briefcase size={18} /> Services</span>
                {servicesOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
              </button>
              {servicesOpen && (
                <div className="pl-3 space-y-1">
                  {visibleServices.map((s) => (
                    <NavItem key={s} to={`/services/${encodeURIComponent(s)}`} icon={Briefcase} emoji={SERVICE_EMOJI[s]} label={s} testId={`nav-service-${s}`} />
                  ))}
                </div>
              )}
            </>
          )}

          {isAdmin && (
            <>
              <NavItem to="/employees" icon={UserCog} label="Process" testId="nav-employees" />
              <NavItem to="/users" icon={Users} label="Connectors" testId="nav-users" />
              <NavItem to="/payments" icon={Receipt} label="Payments" testId="nav-payments" />
            </>
          )}
          {isEmployee && perms.includes("view_payments") && (
            <NavItem to="/payments" icon={Receipt} label="Payments" testId="nav-payments" />
          )}
          {isConnector && (
            <NavItem to="/wallet" icon={Wallet} label="Wallet & Pay" testId="nav-wallet" />
          )}

          {(isAdmin || isConnector || (isEmployee && perms.includes("chat_admin"))) && (
            <NavItem to="/chat" icon={MessageSquare} label={isAdmin ? "Chat" : "Chat with Admin"} testId="nav-chat" />
          )}
          <NavItem to="/tickets" icon={Ticket} label="Support Tickets" testId="nav-tickets" />
          {(isAdmin || (isEmployee && perms.includes("view_reports"))) && (
            <NavItem to="/reports" icon={BarChart3} label="Reports" testId="nav-reports" />
          )}
          {(isAdmin || (isEmployee && perms.includes("view_reports"))) && (
            <NavItem to="/reports/connectors" icon={Users2} label="Connector Reports" testId="nav-connector-reports" />
          )}
          {(isAdmin || isEmployee) && (
            <>
              <NavItem to="/invoices" icon={FileText} label="Invoices" testId="nav-invoices" />
              <NavItem to="/quotations" icon={FileText} label="Quotations" testId="nav-quotations" />
            </>
          )}

          <div className="text-[10px] uppercase tracking-wider text-muted-foreground px-3 pt-4 pb-1">Account</div>
          {isAdmin && (
            <NavItem to="/activity" icon={Activity} label="My Activity" testId="nav-activity" />
          )}
          <NavItem to="/settings" icon={Settings} label="Settings" testId="nav-settings" />
        </nav>

        <div className="p-3 border-t border-border">
          <div className="flex items-center gap-3 px-2 py-2">
            <div className="w-9 h-9 rounded-full bg-accent text-accent-foreground flex items-center justify-center font-semibold uppercase text-sm">
              {user?.name?.[0] || "U"}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold truncate" data-testid="sidebar-user-name">{user?.name}</div>
              <div className="text-[11px] text-muted-foreground capitalize">{user?.role}</div>
            </div>
            <button className="btn btn-ghost !p-2" onClick={async () => { await logout(); navigate("/login"); }} data-testid="btn-logout" title="Logout">
              <LogOut size={16} />
            </button>
          </div>
        </div>
      </aside>

      <main className="flex-1 flex flex-col min-w-0">
        <header className="h-16 border-b border-border bg-card/80 backdrop-blur-md px-6 flex items-center justify-between sticky top-0 z-20">
          <div className="flex items-center gap-3">
            <Building2 size={18} className="text-muted-foreground" />
            <span className="text-sm text-muted-foreground">Welcome back,</span>
            <span className="font-semibold">{user?.name}</span>
            <span className="text-xs px-2 py-0.5 rounded-full bg-accent text-accent-foreground font-semibold capitalize">{user?.role}</span>
          </div>

          <div className="flex items-center gap-3">
            <button onClick={toggle} className="btn btn-ghost !p-2" data-testid="btn-theme-toggle" aria-label="Toggle theme">
              {theme === "dark" ? <Sun size={18} /> : <Moon size={18} />}
            </button>

            <div className="relative">
              <button className="btn btn-ghost !p-2 relative" onClick={() => { setNotifOpen((v) => !v); if (!notifOpen) markRead(); }} data-testid="btn-notifications">
                <Bell size={18} />
                {notifs.unread > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center" data-testid="notif-unread-count">
                    {notifs.unread}
                  </span>
                )}
              </button>
              {notifOpen && (
                <div className="absolute right-0 mt-2 w-80 card fade-in z-30" data-testid="notif-dropdown">
                  <div className="p-3 border-b border-border font-semibold">Notifications</div>
                  <div className="max-h-96 overflow-y-auto">
                    {notifs.items.length === 0 ? (
                      <div className="p-6 text-center text-sm text-muted-foreground">No notifications</div>
                    ) : notifs.items.map((n) => (
                      <div key={n.id} className="p-3 border-b border-border last:border-0 hover:bg-muted/50">
                        <div className="text-sm font-semibold">{n.title}</div>
                        <div className="text-xs text-muted-foreground mt-0.5">{n.message}</div>
                        <div className="text-[10px] text-muted-foreground mt-1">{new Date(n.timestamp).toLocaleString()}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </header>

        <div className="flex-1 p-6 overflow-y-auto fade-in">{children}</div>
      </main>
    </div>
  );
}
